import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { addToCart } from '@/lib/cartStore';
import { toast } from '@/hooks/use-toast';
import { fireEvent } from '@/lib/analytics';
import LeaseCalculator from '@/components/lease/LeaseCalculator';
import LeaseTerms from '@/components/lease/LeaseTerms';
import { Seo, withTemplate, canonicalize } from '@/lib/seo';
import { breadcrumbJsonLd } from '@/lib/structuredData';
import JsonLD from '@/components/JsonLD';
import { useParallaxRef } from '@/hooks/useParallax';

interface DomainApiData {
  domain: string;
  bin_price: number;
  tld: string;
  availability_bin: boolean;
  availability_offer: boolean;
}

export default function DomainLease() {
  const { name } = useParams<{ name: string }>();
  const navigate = useNavigate();
  const [domainData, setDomainData] = useState<DomainApiData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Parallax refs - must be called before any conditional returns
  const heroTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 60 });
  const heroSubRef = useParallaxRef({ speed: 0.08, axis: 'y', maxTranslate: 40 });

  useEffect(() => {
    const fetchDomainData = async () => {
      if (!name) {
        setError('Domain name required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Clean domain name (remove protocol and www)
        const cleanDomain = name.replace(/^(https?:\/\/)?(www\.)?/, '').toLowerCase().trim();
        
        const { data, error: supabaseError } = await supabase
          .from('domains')
          .select('domain, bin_price, tld, availability_bin, availability_offer')
          .ilike('domain', cleanDomain)
          .maybeSingle();

        if (supabaseError || !data) {
          setError('Domain not found');
          setDomainData(null);
        } else {
          // Validate BIN pricing exists and is valid
          if (!data.bin_price || data.bin_price <= 0) {
            setError('Lease pricing unavailable for this domain');
            setDomainData(null);
          } else {
            setDomainData(data);
          }
        }
      } catch (err) {
        console.error('Error loading domain data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load domain data');
      } finally {
        setLoading(false);
      }
    };

    fetchDomainData();
  }, [name]);

  const handleAddToCart = (term: number, monthlyAmount: number) => {
    if (!domainData) return false;

    const success = addToCart({
      domain: domainData.domain,
      tld: domainData.tld || domainData.domain.split('.').pop() || '',
      binPrice: domainData.bin_price,
      lease: true,
      leaseTerm: term,
      leaseMonthly: monthlyAmount,
    });

    if (success) {
      toast({
        title: 'Added to cart',
        description: `${domainData.domain} lease (${term} months) added to your cart.`,
      });
      // Only fire analytics on successful cart addition
      fireEvent('add_to_cart', {
        domain_id: domainData.domain,
        price: domainData.bin_price,
        lease: true,
        lease_term: term,
        lease_monthly: monthlyAmount,
        src: 'domain_lease_page',
        success: true,
      });
      navigate('/cart');
    } else {
      toast({
        title: 'Already in cart',
        description: `${domainData.domain} is already in your cart.`,
        variant: 'destructive',
      });
      // Track failed attempt (already in cart)
      fireEvent('add_to_cart_failed', {
        domain_id: domainData.domain,
        lease: true,
        reason: 'already_in_cart',
      });
    }
    
    return success;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-4 text-muted-foreground">Loading lease details...</p>
        </div>
      </div>
    );
  }

  if (error || !domainData) {
    const isPricingError = error?.includes('pricing unavailable');
    
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md text-center space-y-6">
          <h1 className="text-4xl font-bold">
            {isPricingError ? 'Lease Unavailable' : 'Domain Not Found'}
          </h1>
          <p className="text-lg text-muted-foreground">
            {error || 'The requested domain could not be found.'}
          </p>
          {isPricingError && (
            <div className="bg-muted/50 p-4 rounded-lg text-sm text-left space-y-2">
              <p className="font-medium">Alternative options:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>View domain details and purchase outright</li>
                <li>Make an offer with custom terms</li>
                <li>Contact us for special financing</li>
              </ul>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {isPricingError && name && (
              <Link to={`/domain/${name}`}>
                <Button className="w-full sm:w-auto">View Domain Details</Button>
              </Link>
            )}
            <Link to="/domains">
              <Button variant={isPricingError ? 'outline' : 'default'} className="w-full sm:w-auto">
                Browse Domains
              </Button>
            </Link>
            <Button variant="outline" onClick={() => navigate(-1)} className="w-full sm:w-auto">
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // Additional validation: check if domain is available for purchase
  const isAvailableForLease = domainData.bin_price > 0 && (domainData.availability_bin || domainData.availability_offer);

  const domain = domainData.domain;
  const canonicalUrl = canonicalize(`/domain/${domain}/lease`);

  return (
    <>
      <Seo
        title={withTemplate(`${domain} — Lease to Own`)}
        description={`Lease ${domain} with flexible monthly payments. Start using immediately with full DNS control. Buy at BIN price anytime.`}
        canonical={canonicalUrl}
        og={{
          title: `${domain} — Lease to Own`,
          description: `Lease ${domain} with monthly payments. Buy anytime at BIN price.`,
          url: canonicalUrl,
        }}
        twitter={{
          card: 'summary',
          title: `${domain} — Lease to Own`,
          description: `Lease ${domain} with monthly payments. Buy anytime at BIN price.`,
        }}
      />

      <JsonLD
        data={breadcrumbJsonLd([
          { name: 'Home', url: canonicalize('/') },
          { name: 'Domains', url: canonicalize('/domains') },
          { name: domain, url: canonicalize(`/domain/${domain}`) },
          { name: 'Lease to Own', url: canonicalUrl },
        ])}
      />

      <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 py-8 px-4">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Breadcrumb */}
          <div className="mb-4 stagger-fade-1">
            <Link
              to={`/domain/${domain}`}
              className="inline-flex items-center text-muted-foreground hover:text-primary transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to {domain}
            </Link>
          </div>

          {/* Header */}
          <div className="text-center space-y-4 stagger-fade-1">
            <h1
              ref={heroTitleRef as React.RefObject<HTMLHeadingElement>}
              className="text-4xl lg:text-5xl font-bold text-foreground"
            >
              Lease {domain} to Own
            </h1>
            <p
              ref={heroSubRef as React.RefObject<HTMLParagraphElement>}
              className="text-lg text-muted-foreground max-w-2xl mx-auto"
            >
              Start using your domain immediately with flexible monthly payments. 
              Get full DNS control from day one and buy at the BIN price anytime.
            </p>
          </div>

          {/* Availability Warning */}
          {!isAvailableForLease && (
            <Card className="apple-card border-orange-200 bg-orange-50 stagger-fade-2 animate-fade-in-up">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="text-orange-600 mt-0.5">⚠️</div>
                  <div>
                    <p className="font-medium text-orange-900">Limited Availability</p>
                    <p className="text-sm text-orange-800 mt-1">
                      This domain may not be immediately available for lease. Please contact me to discuss options.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Lease Calculator & Terms */}
          <div className="grid md:grid-cols-2 gap-6 stagger-fade-2">
            <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <LeaseCalculator
                binPrice={domainData.bin_price}
                domain={domainData.domain}
                onAddToCart={handleAddToCart}
                disabled={!isAvailableForLease}
              />
            </div>
            <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <LeaseTerms />
            </div>
          </div>

          {/* Additional Info */}
          <Card className="apple-card stagger-fade-3 animate-fade-in-up">
            <CardHeader>
              <CardTitle className="text-2xl font-semibold">How It Works</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div>
                  <h3 className="font-semibold text-foreground mb-1">1. Choose Your Lease Term</h3>
                  <p className="text-sm text-muted-foreground">
                    Select 12, 24, or 36 months. Monthly payments include a 3% processing fee.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">2. Start Using Immediately</h3>
                  <p className="text-sm text-muted-foreground">
                    Get full DNS and hosting control right away. Build your website, set up email, 
                    and use the domain like you own it.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">3. Buy Anytime at BIN Price</h3>
                  <p className="text-sm text-muted-foreground">
                    Exercise your option to purchase the domain at the original BIN price 
                    (${domainData.bin_price.toLocaleString()}) at any time during your lease.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">4. Full Ownership After Completion</h3>
                  <p className="text-sm text-muted-foreground">
                    Complete all lease payments to receive full domain ownership and transfer.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTA Section */}
          <div className="text-center space-y-4 stagger-fade-4">
            <p className="text-muted-foreground">
              Ready to start leasing? Use the calculator above to select your term and add to cart.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={`/domain/${domain}`}>
                <Button variant="outline" size="lg" className="apple-button-secondary-cta w-full sm:w-auto">
                  View Full Details
                </Button>
              </Link>
              <Link to="/domains">
                <Button size="lg" className="w-full sm:w-auto">
                  Browse More Domains
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
